import { create, show, useModal } from "@ebay/nice-modal-react";
import { useCallback } from "react";

import { PollingIntervalGroups } from "apollo/constants";
import FlashContext from "components/FlashMessages/FlashContext";
import ConfirmationModal from "ds/components/ConfirmationModal";
import { ConfirmationModalMessage } from "ds/components/ConfirmationModal/Message";
import useTypedContext from "hooks/useTypedContext";
import { pluralizeNext } from "shared/Pluralize";

import useOpenTofuMigrationClearQueue from "./useClearQueue";
import useOpenTofuMigrationCounts from "./useCounts";

type OpenTofuMigrationClearQueueConfirmationModalProps = {
  refetchQueries: string[];
  onCleared?: () => void;
};

const OpenTofuMigrationClearQueueConfirmationModal = create(
  function OpenTofuMigrationClearQueueConfirmationModal({
    refetchQueries,
    onCleared,
  }: OpenTofuMigrationClearQueueConfirmationModalProps) {
    const modal = useModal();
    const { reportInfo, reportSuccess } = useTypedContext(FlashContext);

    const { pendingEntries } = useOpenTofuMigrationCounts(PollingIntervalGroups.Lists);

    const [clearQueue, { loading }] = useOpenTofuMigrationClearQueue({ refetchQueries });

    const handleConfirm = useCallback(() => {
      clearQueue({}, (data) => {
        if (data?.openTofuMigrationQueueClean) {
          const { removed } = data.openTofuMigrationQueueClean;

          if (removed === 0) {
            reportInfo({ message: "No pending entries were removed." });
          } else {
            reportSuccess({
              message: `${pluralizeNext(removed, "stack")} removed from the migration queue`,
            });
            onCleared?.();
          }

          modal.resolve();
          modal.hide();
        }
      });
    }, [clearQueue, modal, onCleared, reportInfo, reportSuccess]);

    return (
      <ConfirmationModal
        title="Clear pending entries"
        confirmationButtonLabel="Clear pending"
        confirmationButtonVariant="dangerPrimary"
        onConfirm={handleConfirm}
        isLoading={loading}
        isDismissable={!loading}
      >
        <ConfirmationModalMessage>
          Are you sure you want to clear pending entries? {pluralizeNext(pendingEntries, "stack")}{" "}
          will not be migrated to OpenTofu or rolled back to Terraform.
        </ConfirmationModalMessage>
      </ConfirmationModal>
    );
  }
);

export const showOpenTofuMigrationClearQueueConfirmationModal = (
  props: OpenTofuMigrationClearQueueConfirmationModalProps
) => show(OpenTofuMigrationClearQueueConfirmationModal, props);
